import * as React from "react";
import { useMappedState } from "redux-react-hook";
import styled, { css } from "styled-components";
import { toast } from "react-toastify";
import { capitalize } from "lodash";
import * as _ from "lodash";

import { PaginatedParams } from "src/library/utils";
import {
  ActionButton,
  Button
} from "src/library/components/Button";
import LikeToDo from "src/library/components/LikeToDo/LikeToDo";
import Table, { ITableInstance } from "src/library/components/Tables/Table";
import { generatePageSizeOptions } from "src/library/components/Tables/utils";
import { ProductCard } from "src/core/products/components/ProductCard";
import { ImportModal } from "src/core/shared/components/ImportModal";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { productTableColumns } from "src/core/shared/components/tables";
import {
  useProductsApi,
  IProduct,
  ListProductsFilters
} from "src/core/products/api/products.api";
import type { IStore } from "src/core/shared/store";
import { PageProps } from "src/pages/Router/routes";
import { ProductExportModal } from "src/core/products/components/ProductExportModal";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import { Text } from "src/library/components/Text";
import { CheckboxStyled } from "src/library/components/Forms";
import { useNutritionApi } from "src/modules/nutrition/api/nutrition.api";
import {
  ERPOdoo,
  ERPUnity
} from "src/utils/constants";
import { ConnectorProductImportModal } from "src/modules/connector/components/ConnectorProductImportModal";
import { useConnectorApi } from "src/modules/connector/api/connector.api";
import {
  ImportOrigin,
  ImportSettingType,
  useImportSettingApi
} from "src/core/shared/api/importSetting.api";

const defaultPagination: PaginatedParams<IProduct> = {
  page: 0,
  limit: 12,
  offset: 0,
  sort: [ [ "description", "ASC" ] ]
};

/**
 * Main "Products" View
 *
 * @returns
 */
const ProductsManagementPage: React.ReactFCWithChildren<PageProps<unknown>> = props => {
  const TableRef = React.useRef<ITableInstance<IProduct>>(null);
  const defaultFilters = {};
  const [ showImportModal, setShowImportModal ] = React.useState(false);
  const [ showNutritionImportModal, setShowNutritionImportModal ] = React.useState(false);
  const [ showExportModal, setShowExportModal ] = React.useState(false);
  const [ displayDelistedProducts, setDisplayDelistedProducts ] = React.useState(false);
  const [ displayUnpublishedProducts, setDisplayUnpublishedProducts ] = React.useState(false);
  const [ exportNutritionProductsResponse, exportNutritionProducts ] = useNutritionApi("EXPORT_PRODUCT_NUTRITION");
  const [ syncProductResponse, syncProductRequest ] = useConnectorApi("IMPORT_API_PRODUCT");
  const [ triggerFtpImportResponse, triggerFtpImportRequest ] = useImportSettingApi("TRIGGER_FTP_IMPORT");
  const [ showERPImportModal, setShowERPImportModal ] = React.useState(false);

  const {
    currentRole, installedModules, importSettings, erp
  } = useMappedState(({ global }: IStore) => ({
    currentRole: global.role,
    erp: global.settings && global.settings.erp.name,
    installedModules: global.settings?.installedModules,
    importSettings: global.settings?.importSettings
  }));

  const { terminology } = props;
  const [ listProductsResponse, listProducts ] = useProductsApi("LIST_PRODUCTS");
  const productsData = listProductsResponse.data;
  const checkPermission = useCheckUserPermission();

  const canCreateProduct = React.useMemo(() => {
    return checkPermission("create-product");
  }, [ checkPermission ]);

  React.useEffect(() => {
    if (triggerFtpImportResponse.data) {
      toast.success(triggerFtpImportResponse.data.message);
    } else if (triggerFtpImportResponse.error) {
      toast.error(triggerFtpImportResponse.error.message);
    }
  }, [ triggerFtpImportResponse.data, triggerFtpImportResponse.error ]);

  React.useEffect(() => {
    if (exportNutritionProductsResponse.data) {
      toast.success(exportNutritionProductsResponse.data.message);
    } else if (exportNutritionProductsResponse.error) {
      toast.error(exportNutritionProductsResponse.error.message);
    }
  }, [ exportNutritionProductsResponse ]);

  const onFetchData = React.useCallback((filters: ListProductsFilters, pagination: PaginatedParams<IProduct>) => {
    listProducts({
      filters: {
        ...filters,
        includeDelisted: displayDelistedProducts,
        includeUnpublished: displayUnpublishedProducts
      },
      pagination
    });
  }, [
    displayDelistedProducts,
    displayUnpublishedProducts,
    listProducts
  ]);

  React.useEffect(() => {
    if (TableRef.current) {
      TableRef.current.reloadTable();
    }
  }, [ ]);

  React.useEffect(() => {
    if (TableRef.current) {
      TableRef.current.reloadTable();
    }
  }, [ displayDelistedProducts ]);

  React.useEffect(() => {
    if (TableRef.current) {
      TableRef.current.reloadTable();
    }
  }, [ displayUnpublishedProducts ]);

  /**
   * Returns the buttons to go in the like to do actions
   */
  const ProductButtons = React.useMemo(() => {
    const buttons = [
      <Button
        key="export-products"
        onClick={() => setShowExportModal(true)}
      >
        {`Export ${terminology.product.plural.text}`}
      </Button>

    ];

    const hasProductCSVImportSetting = _.find(importSettings, is => {
      return is.type === ImportSettingType.Product && is.origin === ImportOrigin.CSV && !is.options?.exportOnly;
    });

    if (hasProductCSVImportSetting && canCreateProduct) {
      buttons.push((
        <Button
          key="create-product"
          onClick={() => props.history.push("/admin/products/create")}
        >
          {`Create new ${
            terminology.product.singular.text
          }`}
        </Button>
      ));

      buttons.push((
        <Button
          key="import-products"
          onClick={() => setShowImportModal(true)}
        >
          {`Import ${terminology.product.plural.text}`}
        </Button>
      ));
    }

    if (installedModules?.includes("nutrition")) {
      buttons.push((
        <Button
          key="import-nutrition"
          onClick={() => setShowNutritionImportModal(true)}
        >
          Import Nutrition
        </Button>
      ));

      buttons.push((
        <Button
          key="export-nutrition"
          onClick={() => exportNutritionProducts(null)}
          loading={exportNutritionProductsResponse.pending}
        >
          Export Nutrition
        </Button>
      ));
    }

    // currently only allow unity to import individual product
    if (erp === ERPUnity || erp === ERPOdoo) {
      buttons.unshift(
        <Button
          key="import-product"
          onClick={() => setShowERPImportModal(true)}
        >
          {`Import From ${capitalize(erp)}`}
        </Button>);
    }
    const hasProductSFTPImportSetting = _.find(importSettings, is => is.type === ImportSettingType.Product && is.origin === ImportOrigin.SFTP);

    if (hasProductSFTPImportSetting && canCreateProduct) {
      buttons.push((
        <Button
          key="create-product"
          onClick={() => triggerFtpImportRequest(hasProductSFTPImportSetting.id)}
        >
          {`Sync ${
            terminology.product.singular.text
          } from FTP`}
        </Button>
      ));
    }

    return buttons;
  }, [
    terminology.product.plural.text,
    terminology.product.singular.text,
    importSettings,
    canCreateProduct,
    installedModules,
    erp,
    props.history,
    exportNutritionProductsResponse.pending,
    exportNutritionProducts,
    triggerFtpImportRequest
  ]);

  const returnActions = (row: any) => {
    const canAmendProduct = checkPermission("amend-product");

    const actions = [
      {
        title: `View ${terminology.product.singular.text}`,
        action: () => props.history.push(`/products/${row.original.id}`)
      }
    ];

    if (canAmendProduct) {
      actions.push({
        title: `Edit ${terminology.product.singular.text}`,
        action: () => props.history.push(`/admin/products/${row.original.id}/details`)
      });
    }

    // currently only allow unity and odoo to import individual product
    if (erp === ERPUnity || erp === ERPOdoo) {
      actions.push({
        title: `Import from ${capitalize(erp)}`,
        action: () => {
          syncProductRequest({ partNumber: row.original.partNumber });
        }
      });
    }

    return actions;
  };

  /** Handle the api product import response by showing a toast */
  React.useEffect(() => {
    if (syncProductResponse.data) {
      toast.success("Product import has been added to the job queue and will be processed shortly");
    } else if (syncProductResponse.error) {
      toast.error(syncProductResponse.error.message);
    }
  }, [ syncProductResponse ]);

  return (
    <Layout {...props}>
      <MainContentWrapper
        fill
        Header={(
          <LikeToDo
            copy="What would you like to do?"
            buttons={ProductButtons}
          />
        )}
      >
        <AdditionalFiltersStyled className="additional-filters">
          <Text weight="bold">
            Include delisted products:
          </Text>

          <CheckboxStyled
            checked={displayDelistedProducts}
            onChange={e => setDisplayDelistedProducts(e.target.checked)}
          />

          <Text
            weight="bold"
            style={{ marginLeft: "30px" }}
          >
            Include unpublished products:
          </Text>

          <CheckboxStyled
            checked={displayUnpublishedProducts}
            onChange={e => setDisplayUnpublishedProducts(e.target.checked)}
          />
        </AdditionalFiltersStyled>

        <Table
          tableId="global"
          ref={TableRef}
          mainAction={row => props.history.push(`/products/${row.original.id}`)}
          tableOf={terminology.product.plural.text}
          view="list"
          viewAllowedToToggle
          data={productsData ? productsData.result : []}
          error={listProductsResponse.error}
          onFetchData={onFetchData}
          defaultFilters={defaultFilters}
          defaultPagination={defaultPagination}
          pageSizeOptions={generatePageSizeOptions(12)}
          loading={listProductsResponse.pending}
          pages={productsData ? productsData.pages : 0}
          totalRows={productsData ? productsData.count : 0}
          columns={productTableColumns({
            ActionButton: row => (
              <ActionButton
                loading={syncProductResponse.pending}
                title="actions"
                items={returnActions(row)}
              />
            ),
            GridViewComponent: product => (
              <ProductCard
                view="grid"
                product={product}
                productImage={product.image || ""}
                drop={null}
              />
            ),
            terminology: props.terminology,
            currentRole,
            pricesVisible: false
          })}
        />

        <ImportModal
          open={showImportModal}
          onDismiss={() => setShowImportModal(false)}
          type="product"
          terminology={props.terminology}
        />

        <ImportModal
          open={showNutritionImportModal}
          onDismiss={() => setShowNutritionImportModal(false)}
          type="nutrition"
          terminology={props.terminology}
        />

        <ProductExportModal
          open={showExportModal}
          onDismiss={() => setShowExportModal(false)}
        />

        <ConnectorProductImportModal
          open={showERPImportModal}
          onDismiss={() => setShowERPImportModal(false)}
        />
      </MainContentWrapper>
    </Layout>
  );
};

const AdditionalFiltersStyled = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;

    input {
      margin-left: ${theme.spacing.margins.md};

      &:hover {
        cursor: pointer;
      }
    }
  `}  
`;

export default ProductsManagementPage;
